// Customer-side LiveChat client. Thin wrapper over POST /livechat/customer/escalate so the
// "Talk to a human" button can transition the chat into the agent queue. Mirrors the backend
// behaviour described in webapi.LiveChat/Controllers/LiveChatCustomerController.cs:
// chatId must be a valid GUID, 200 returns the updated mode doc, 409 means the chat is
// already queued / handled, 404 means the chat is not owned by the caller.
import { HostContext } from '../../embed-script/HostContext';
import { SupportedImageMime } from '../utils/imageUtils';
import { BackendServiceUrl, BaseService } from './BaseService';

// Wire values of EscalateConflictResponse.reason — mirror of webapi.LiveChat/Models/EscalateConflictReasons.cs.
// Only AlreadyQueued/AlreadyHandled mean the chat is genuinely in progress; NoAgentsOnline, QueueFull and
// ConcurrentlyModified queued nothing, so the UI must not imply a human is on the way.
export const EscalateConflictReasons = {
    AlreadyQueued: 'already_queued',
    AlreadyHandled: 'already_handled',
    QueueFull: 'queue_full',
    NoAgentsOnline: 'no_agents_online',
    ConcurrentlyModified: 'concurrently_modified',
} as const;

// The active proactive human-handoff offer for a chat, as returned by
// GET /livechat/customer/offer/{chatId}. A 204 (no active offer) deserializes to {} here, so
// callers must treat a missing messageId as "no offer".
export interface CustomerOfferResponse {
    chatId: string;
    messageId: string;
    offeredAt: string;
}

// The chat's live-chat lifecycle mode. Mirrors the backend LiveChatModeValue enum; 'AI' is the
// implicit default a chat reports when it has no mode doc (never escalated, or back to the bot).
export type CustomerLiveChatMode = 'AI' | 'Queued' | 'Human' | 'Resolved';

// Customer-safe mode projection returned by GET /livechat/customer/mode/{chatId}. The source of
// truth for whether the customer is still queued — used to dismiss the "waiting for a human" hint
// even when the terminal System notice that normally clears it is dropped.
export interface CustomerModeResponse {
    chatId: string;
    mode: CustomerLiveChatMode;
    queuedAt: string | null;
}

// 200 body of POST /livechat/customer/escalate. Mirrors CustomerEscalateResponse in webapi.LiveChat/Models.
export interface CustomerEscalateResponse {
    chatId: string;
    mode: CustomerLiveChatMode;
    queuedAt: string;
    escalationCount: number;
    /** Median queue wait of recent pickups; omitted when there are too few to estimate it. */
    expectedWaitSeconds?: number;
}

// Body of POST /livechat/customer/chats/{chatId}/screenshots — same shape as the AI image-paste
// request (base64 without the data-URL prefix + one of the three supported MIME types).
export interface ScreenshotUpload {
    base64: string;
    mimeType: SupportedImageMime;
    /** True when answering an agent's request; the backend then adds a "shared" notice for the agent. */
    requested?: boolean;
}

// 201 body of the screenshot upload. Mirrors ScreenshotUploadResponse in webapi.LiveChat/Models.
export interface ScreenshotUploadResponse {
    screenshotId: string;
    timestamp: string;
}

export class CustomerLiveChatService extends BaseService {
    constructor(accessToken: string, baseServiceUrl: string = BackendServiceUrl) {
        super(accessToken, baseServiceUrl);
    }

    // hostContext rides on every call: the customer escalation surface is gated server-side on the
    // customer group (HumanHandoverEligibility), which is read from the AiaHostContext header. Omit
    // it and the gate sees a standalone context and conceals the surface behind a 404.
    public escalateAsync = async (chatId: string, hostContext?: HostContext): Promise<CustomerEscalateResponse> => {
        return this.getResponseAsync<CustomerEscalateResponse>({
            commandPath: 'livechat/customer/escalate',
            method: 'POST',
            body: { chatId },
            hostContext,
        });
    };

    // Closes the customer's currently agent-handled chat when they start a new one (#1937): the
    // backend marks it resolved as "user closed chat", pushes a status message to the agent and drops
    // it from the agent's active list. 204 on success; 404 (not owned) / 409 (not agent-handled) are
    // benign here — the caller proceeds to create the new chat regardless (the server-side lock on
    // the old chat is the real guard).
    public closeAsync = async (chatId: string, hostContext?: HostContext): Promise<unknown> => {
        return this.getResponseAsync({
            commandPath: 'livechat/customer/close',
            method: 'POST',
            body: { chatId },
            hostContext,
        });
    };

    // Reload-survival lookup: returns the active (non-dismissed) offer, or {} when there is none.
    public getOfferAsync = async (
        chatId: string,
        hostContext?: HostContext,
    ): Promise<Partial<CustomerOfferResponse>> => {
        return this.getResponseAsync<Partial<CustomerOfferResponse>>({
            commandPath: `livechat/customer/offer/${chatId}`,
            method: 'GET',
            hostContext,
        });
    };

    public dismissOfferAsync = async (chatId: string, hostContext?: HostContext): Promise<unknown> => {
        return this.getResponseAsync({
            commandPath: `livechat/customer/offer/${chatId}/dismiss`,
            method: 'POST',
            hostContext,
        });
    };

    // Reads the chat's current live-chat mode. Used as a robust fallback to dismiss the queued
    // waiting hint when the terminal System notice was dropped — the UI polls this while queued.
    public getModeAsync = async (chatId: string, hostContext?: HostContext): Promise<CustomerModeResponse> => {
        return this.getResponseAsync<CustomerModeResponse>({
            commandPath: `livechat/customer/mode/${chatId}`,
            method: 'GET',
            hostContext,
        });
    };

    // Uploads a screenshot into an agent-handled chat. 201 on success; 400 for an invalid or
    // oversized image, 404 when the chat isn't the caller's, 409 when no agent is handling the chat.
    public uploadScreenshotAsync = async (
        chatId: string,
        image: ScreenshotUpload,
        hostContext?: HostContext,
    ): Promise<ScreenshotUploadResponse> => {
        const response = await this.getResponseAsync<Partial<ScreenshotUploadResponse>>({
            commandPath: `livechat/customer/chats/${chatId}/screenshots`,
            method: 'POST',
            body: image,
            hostContext,
        });
        // The base client resolves a 429 with an empty payload after raising the rate-limit
        // alert; without a screenshot id nothing was stored, so callers must not echo it as sent.
        if (!response.screenshotId) {
            throw new Error('Screenshot upload was not accepted');
        }
        return response as ScreenshotUploadResponse;
    };

    // Records that the customer dismissed the agent's screenshot request so the agent sees a
    // notice instead of an unanswered prompt. Best-effort from the caller's point of view: 204 on
    // success, 404 (not owned) / 409 (not agent-handled) are benign.
    public declineScreenshotAsync = async (chatId: string, hostContext?: HostContext): Promise<unknown> => {
        return this.getResponseAsync({
            commandPath: `livechat/customer/chats/${chatId}/screenshots/decline`,
            method: 'POST',
            hostContext,
        });
    };

    // Probes whether the signed-in user is an allow-listed live-chat agent, so the manual escalate
    // quick button (a staff-only affordance gated server-side the same way) can stay hidden for
    // everyone else. GET /livechat/agent/me returns the agent identity for an allow-listed caller
    // and 403 otherwise. Fails safe to hidden: a 403 or transient error is caught, and an empty
    // body is rejected too — BaseService maps a 429 to `{}` without throwing, which must not read
    // as "allowed".
    public isAllowedAgentAsync = async (): Promise<boolean> => {
        try {
            const me = await this.getResponseAsync<{ email?: string }>({
                commandPath: 'livechat/agent/me',
                method: 'GET',
            });
            return typeof me.email === 'string' && me.email.length > 0;
        } catch {
            return false;
        }
    };
}
